import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, KeyboardBadge, token } from '@bynder/design-system';
import { IconHelpOutline } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';

import { VIEWPORT_WIDTH_BREAKPOINTS } from '../constants';
import { HelpModal } from './HelpModal';
import { BREAKPOINTS } from '../../../breakpoints';

export const HELP_FOOTER_HEIGHT = 48;

type Props = {
	showSearchKeyboardShortcut: boolean;
};

export function HelpFooter({ showSearchKeyboardShortcut }: Props) {
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const { translate } = useTranslate();

	return (
		<>
			<FooterContainer>
				<Button
					onClick={() => setIsHelpModalOpen(true)}
					variant="clean"
					isSmall
					icon={<IconHelpOutline />}
				>
					{translate('spotlight.dropdown.help')}
				</Button>
				<NavigationKeysContainer>
					{showSearchKeyboardShortcut && (
						// DS KeyboardBadge has no search icon yet; revisit with the DS team if one gets added.
						<div>
							<KeyboardBadge>/</KeyboardBadge>
							<StyledShortcutLabel>
								{translate('spotlight.dropdown.footer.search')}
							</StyledShortcutLabel>
						</div>
					)}
					<div>
						<KeyboardBadge icon="up" isLabelHidden>
							{translate('spotlight.dropdown.footer.up')}
						</KeyboardBadge>
						<KeyboardBadge icon="down" isLabelHidden>
							{translate('spotlight.dropdown.footer.down')}
						</KeyboardBadge>
						<StyledShortcutLabel>
							{translate('spotlight.dropdown.footer.navigate')}
						</StyledShortcutLabel>
					</div>
					<div>
						<KeyboardBadge icon="enter" isLabelHidden>
							{translate('spotlight.dropdown.footer.select')}
						</KeyboardBadge>
						<StyledShortcutLabel>
							{translate('spotlight.dropdown.footer.select')}
						</StyledShortcutLabel>
					</div>
					<div>
						<KeyboardBadge>esc</KeyboardBadge>
						<StyledShortcutLabel>
							{translate('spotlight.dropdown.footer.close')}
						</StyledShortcutLabel>
					</div>
				</NavigationKeysContainer>
			</FooterContainer>

			<HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
		</>
	);
}

const FooterContainer = styled.div`
	z-index: 1;
	margin: 0;
	padding: ${token.spacing3} ${token.spacing4};
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: ${token.gray25};
	height: ${HELP_FOOTER_HEIGHT}px;
	max-height: ${HELP_FOOTER_HEIGHT}px;
	position: absolute;
	bottom: -1px;
	left: 0;
	right: 0;
`;

const StyledShortcutLabel = styled.span``;

// !important used to override the default css
const NavigationKeysContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	color: ${token.colorTextMuted};
	font-size: 14px !important;

	& > div {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	@media (max-width: ${VIEWPORT_WIDTH_BREAKPOINTS.MEDIUM - 1}px) {
		${StyledShortcutLabel} {
			display: none;
		}
	}

	@media (max-width: ${BREAKPOINTS.TABLET}px) {
		display: none;
	}
`;
